// app/frontend/components/EventForm/utils/editEventTransform.ts

import type { EventFormValues } from '../types';

/**
 * Transforms Rails event data into EventForm initialValues format
 * Handles the conversion from edit mode data to form-expected structure
 */
export function transformEditEventData(railsEventData: any): EventFormValues {
  // Helper function to extract time from datetime string
  const extractTime = (datetimeString: string | null): string => {
    if (!datetimeString) return '';
    try {
      // If it's already just a time string (HH:MM), return it
      if (/^\d{2}:\d{2}$/.test(datetimeString)) {
        return datetimeString;
      }
      // If it's a full datetime, extract the time portion
      const date = new Date(datetimeString);
      return date.toTimeString().slice(0, 5); // Gets HH:MM format
    } catch {
      return '';
    }
  };

  // Helper function to extract date from datetime string
  const extractDate = (datetimeString: string | null): string => {
    if (!datetimeString) return '';
    try {
      // If it's already just a date string (YYYY-MM-DD), return it
      if (/^\d{4}-\d{2}-\d{2}$/.test(datetimeString)) {
        return datetimeString;
      }
      // If it's a full datetime, extract the date portion
      const date = new Date(datetimeString);
      return date.toISOString().split('T')[0]; // Gets YYYY-MM-DD format
    } catch {
      return '';
    }
  };

  // Helper function to convert boolean to string format expected by form
  const boolToString = (value: boolean | undefined): string => {
    return value ? 'true' : 'false';
  };

  // Helper function to ensure arrays are properly formatted
  const ensureArray = (value: any): any[] => {
    if (!value) return [];
    return Array.isArray(value) ? value : [];
  };

  // Derive city_data from city object if available
  const cityData = railsEventData.city
    ? {
        id: railsEventData.city.id,
        cityName: railsEventData.city.name || '',
        countryName: railsEventData.city.country_name || '',
        countryCode: railsEventData.city.country_code || '',
        stateName: railsEventData.city.state_name || '',
        areaId: railsEventData.city.area_id || railsEventData.area?.id || '',
        areaName: railsEventData.city.area_name || railsEventData.area?.name || '',
        continentName: railsEventData.city.continent_name || '',
        latitude: railsEventData.city.latitude || null,
        longitude: railsEventData.city.longitude || null,
        cityFound: true,
        missingCityName: null,
      }
    : null;

  // Build the transformed data structure
  const transformedData: EventFormValues = {
    // Basic event info
    id: railsEventData.id || null,
    name: railsEventData.name || '',
    slug: railsEventData.slug || '',
    description: railsEventData.description || '',

    // Location data
    address: railsEventData.address || '',
    city_data: cityData,
    missing_city_detected: false, // Form-specific logic, set to default
    missing_city_name: null, // Form-specific logic, set to default
    timezone: railsEventData.timezone || '',
    online_event: boolToString(railsEventData.online_event),

    // Date and time data
    event_start_date: extractDate(railsEventData.event_start_date),
    event_start_time: railsEventData.event_start_time || extractTime(railsEventData.event_start_date),
    event_end_time: railsEventData.event_end_time || extractTime(railsEventData.event_end_date),
    event_end_date: extractDate(railsEventData.event_end_date),

    // FIXED: Properly map recurring event data
    is_recurring: boolToString(railsEventData.is_recurring),
    spans_next_day: boolToString(railsEventData.spans_next_day),
    recurring_event_id: railsEventData.recurring_event?.id || null, // FIXED: Extract recurring_event_id
    recurring_event_start_date: '', // Not typically provided in edit data
    recurring_event_attributes: {
      start_date: '',
      start_time: '',
      end_time: '',
      recurrence_pattern: '',
      num_occurrences: 0,
      re_timezone: '',
      spans_next_day: false,
      ordinal_occurrence: '',
      occurrence_day: '',
    },
    recurring_event_occurrences: ensureArray(railsEventData.recurring_event_occurrences),
    num_occurrences: 0, // Derived from occurrences length if needed

    // Relationship data (convert to arrays of numbers)
    event_type_ids: ensureArray(railsEventData.event_type_ids),
    artistIds: ensureArray(railsEventData.artist_ids),
    dance_style_ids: ensureArray(railsEventData.dance_style_ids),
    organizer_ids: ensureArray(railsEventData.organizer_ids),

    // Single value relationships
    event_frequency_id: railsEventData.event_frequency_id || '',
    user_id: railsEventData.user_id || null,
    event_status_id: railsEventData.event_status_id || null,

    // URL fields
    website_url: railsEventData.website_url || '',
    ticket_url: railsEventData.ticket_url || '',
    facebook_url: railsEventData.facebook_url || '',
    instagram_url: railsEventData.instagram_url || '',
    meetup_url: railsEventData.meetup_url || '',
    tiktok_url: railsEventData.tiktok_url || '',

    // File upload field
    event_image: null,
    existing_image_url: railsEventData.event_image_url || '',
  };

  return transformedData;
}

/**
 * Gets default values for creating new events
 */
export function getCreateEventDefaults(): EventFormValues {
  return {
    id: null,
    name: '',
    slug: '',
    description: '',
    address: '',
    city_data: null,
    missing_city_detected: false,
    missing_city_name: null,
    timezone: '',
    online_event: 'false',
    is_recurring: 'false',
    spans_next_day: 'false',
    event_start_date: '',
    event_start_time: '',
    event_end_time: '',
    event_end_date: '',
    website_url: '',
    ticket_url: '',
    facebook_url: '',
    instagram_url: '',
    meetup_url: '',
    tiktok_url: '',
    event_type_ids: [],
    artistIds: [],
    dance_style_ids: [],
    event_frequency_id: '',
    user_id: null,
    event_status_id: null,
    organizer_ids: [],
    recurring_event_id: null, // FIXED: Added missing field
    recurring_event_start_date: '',
    recurring_event_attributes: {
      start_date: '',
      start_time: '',
      end_time: '',
      recurrence_pattern: '',
      num_occurrences: 0,
      re_timezone: '',
      spans_next_day: false,
      ordinal_occurrence: '',
      occurrence_day: '',
    },
    recurring_event_occurrences: [],
    num_occurrences: 0,
    event_image: null,
    existing_image_url: '',
  };
}

/**
 * Helper function to determine if we're in edit mode
 */
export function isEditMode(event: any): boolean {
  return Boolean(event && event.id);
}
